import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ShieldAlert, Search, Trash2, AlertTriangle, Database, FlaskConical,
  Eye, RefreshCcw, CheckCircle2,
} from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

// Director-only destructive actions: bulk delete orders, edit, test cleanup.
// All staff can reach this; it's labelled "Director Tools" to mark out that
// the actions on this page are irreversible.
export default function Director() {
  const [rows, setRows] = useState([])
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState(() => new Set())
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [lastAction, setLastAction] = useState(null)

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    setSelected(new Set())
    const { data, error } = await supabase
      .from('orders')
      .select('id, reference, status, placed_at, created_at, customer_po, companies(id, name)')
      .order('created_at', { ascending: false })
      .limit(500)
    if (!error) setRows(data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const filtered = useMemo(() => {
    const s = q.toLowerCase()
    if (!s) return rows
    return rows.filter(r =>
      (r.reference || '').toLowerCase().includes(s)
      || (r.companies?.name || '').toLowerCase().includes(s)
      || (r.customer_po || '').toLowerCase().includes(s)
    )
  }, [rows, q])

  const testOrders = useMemo(
    () => rows.filter(r => /test/i.test(r.customer_po || '') || r.status === 'draft'),
    [rows]
  )

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(r => r.id)))
  }

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const deleteIds = async (ids, label) => {
    if (ids.length === 0) return
    if (isDemoMode) return
    if (!confirm(`Permanently delete ${ids.length} ${label}? This also removes their consignments and line items. Cannot be undone.`)) return
    setDeleting(true)
    setLastAction(null)
    const { error } = await supabase.from('orders').delete().in('id', ids)
    setDeleting(false)
    if (error) {
      setLastAction({ ok: false, msg: error.message })
      return
    }
    setLastAction({ ok: true, msg: `Deleted ${ids.length} ${label}.` })
    await load()
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <DemoNotice />

      <div className="card border-l-4 border-amber-400 bg-amber-50/30">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-600" />
          Director Tools
        </h3>
        <p className="text-sm text-gray-600">
          Destructive actions live here so they're never one click away from a normal page.
          Anything deleted on this page is <strong>permanent</strong> — gone from the database immediately,
          along with the order's consignments and line items.
        </p>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Database className="w-5 h-5 text-brand" />
          Your data is safe across deploys
        </h3>
        <p className="text-sm text-gray-500">
          Every order, product, company, user and setting lives in Supabase, completely separate from
          the deployed app. Deploying a new version of the site only updates the front-end code —
          your records are untouched. Supabase automatically backs everything up daily, restorable
          from the Supabase dashboard.
        </p>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-brand" />
          Quick cleanup
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          Wipes test orders (any with "test" in the customer PO, or still in <em>draft</em> status) in one go.
          Useful after you've been clicking around proving things work.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => deleteIds(testOrders.map(o => o.id), `test/draft order${testOrders.length === 1 ? '' : 's'}`)}
            disabled={deleting || testOrders.length === 0}
            className="btn-danger"
          >
            <Trash2 className="w-4 h-4" />
            {testOrders.length === 0
              ? 'No test orders to delete'
              : `Delete ${testOrders.length} test/draft order${testOrders.length === 1 ? '' : 's'}`
            }
          </button>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-800">All orders ({rows.length})</h3>
          <div className="flex gap-2">
            <button onClick={load} disabled={loading} className="btn-secondary text-sm">
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={() => deleteIds([...selected], `selected order${selected.size === 1 ? '' : 's'}`)}
              disabled={deleting || selected.size === 0}
              className="btn-danger text-sm"
            >
              <Trash2 className="w-4 h-4" />
              {selected.size === 0 ? 'Delete selected' : `Delete ${selected.size} selected`}
            </button>
          </div>
        </div>

        <div className="relative mb-3 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by reference, company, PO…"
            className="input-field pl-9"
          />
        </div>

        {loading && <p className="text-sm text-gray-500">Loading…</p>}

        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">
            {rows.length === 0 ? 'No orders yet.' : 'No orders match the search.'}
          </p>
        )}

        {filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="py-2 w-8">
                    <input type="checkbox"
                           checked={selected.size > 0 && selected.size === filtered.length}
                           onChange={toggleAll} />
                  </th>
                  <th className="text-left py-2">Reference</th>
                  <th className="text-left py-2">Company</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">PO</th>
                  <th className="text-left py-2">Placed</th>
                  <th className="text-right py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const isTest = /test/i.test(o.customer_po || '') || o.status === 'draft'
                  return (
                    <tr key={o.id} className={`border-b border-gray-50 ${isTest ? 'bg-amber-50/30' : ''}`}>
                      <td className="py-2">
                        <input type="checkbox"
                               checked={selected.has(o.id)}
                               onChange={() => toggleOne(o.id)} />
                      </td>
                      <td className="py-2 font-medium text-gray-800">{o.reference || '—'}</td>
                      <td className="py-2 text-gray-700">{o.companies?.name || '—'}</td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          o.status === 'sent'      ? 'bg-brand-50 text-brand-dark' :
                          o.status === 'draft'    ? 'bg-gray-100 text-gray-700' :
                          o.status === 'cancelled' ? 'bg-red-50 text-red-700' :
                          'bg-emerald-50 text-emerald-700'
                        }`}>{o.status}</span>
                      </td>
                      <td className="py-2 text-gray-700">{o.customer_po || '—'}</td>
                      <td className="py-2 text-gray-500 text-xs">
                        {(o.placed_at ? new Date(o.placed_at) : new Date(o.created_at)).toLocaleString()}
                      </td>
                      <td className="py-2 text-right">
                        <Link to={`/orders/${o.id}`} className="text-xs text-brand-dark hover:underline inline-flex items-center gap-1 mr-3">
                          <Eye className="w-3.5 h-3.5" /> View / edit
                        </Link>
                        <button
                          onClick={() => deleteIds([o.id], 'order')}
                          disabled={deleting}
                          className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {lastAction && (
          <div className={`mt-3 p-3 rounded-lg text-sm flex items-start gap-2 ${
            lastAction.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'
          }`}>
            {lastAction.ok
              ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            {lastAction.msg}
          </div>
        )}
      </div>
    </div>
  )
}
